"use client";

import React from "react";
import { UserCheck, Building2, Check, X, Zap, ShieldCheck, ArrowRight, Sparkles, PoundSterling, MessageSquare } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Link } from "@/components/common/Link";
const comparisonPoints = [{
  id: "access",
  title: "Direct Access to Senior Expertise",
  highlight: "You work directly with me",
  consultant: "You deal exclusively with me, Yassine Essaadi. Strategy, technical audits, content direction, and execution are handled personally by a seasoned consultant.",
  agency: "Pitched by senior directors, but day-to-day execution is handed off to junior account managers or entry-level coordinators."
}, {
  id: "cost",
  title: "Maximum Cost Efficiency & Zero Overhead",
  highlight: "100% of budget into SEO execution",
  consultant: "Every pound of your investment pays directly for expert SEO work. You never subsidize costly agency offices, sales commissions, or administrative layers.",
  agency: "Up to 50-60% of your monthly retainer goes toward agency overhead, management fees, and internal operating costs."
}, {
  id: "strategy",
  title: "Agile, Custom-Tailored SEO Strategy",
  highlight: "Bespoke roadmaps that adapt instantly",
  consultant: "I craft bespoke strategies tailored to your exact industry and technical stack. When search algorithms change, I adapt your strategy in real-time.",
  agency: "Rigid monthly checklists and standardized templates designed for high-volume client management rather than tailored growth."
}, {
  id: "accountability",
  title: "Uncompromising Quality & Client Focus",
  highlight: "Strictly capped client roster",
  consultant: "I limit the number of businesses I work with simultaneously. This guarantees your campaigns receive my full, unhurried attention every week.",
  agency: "Account managers are routinely tasked with 15-20+ client accounts, leading to rushed work and generic execution."
}, {
  id: "transparency",
  title: "Clear Communication & Honest Reporting",
  highlight: "Direct Slack & email access",
  consultant: "Plain-English reporting centered on real revenue impact, traffic quality, and search rankings. You have a direct line to ask questions anytime.",
  agency: "Automated, bloated monthly PDF decks filled with vanity metrics designed to obscure what actual work was performed."
}, {
  id: "ai-search",
  title: "Modern AI Search & Technical Mastery",
  highlight: "Proactive AI search optimization",
  consultant: "I continuously integrate cutting-edge AI search optimization alongside rigorous technical SEO to keep your brand visible on Google and AI engines.",
  agency: "Slow to implement new search technologies due to bureaucratic approval processes and outdated agency playbooks."
}];
export default function Benefits() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.1
      }
    }
  };
  const itemVariants = {
    hidden: shouldReduceMotion ? {
      opacity: 1,
      y: 0
    } : {
      opacity: 0,
      y: 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="consultantvsagencybenefits" data-nav="light" className="relative bg-background text-foreground py-16 md:py-24 px-4 sm:px-6 lg:px-8 overflow-hidden">
      {/* Background soft blur accents */}
      <div className="absolute top-1/4 -left-20 w-80 h-80 bg-secondary/60 rounded-full blur-3xl pointer-events-none -z-10" />
      <div className="absolute bottom-10 right-0 w-96 h-96 bg-accent/15 rounded-full blur-3xl pointer-events-none -z-10" />

      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-12 lg:mb-16">
          <Badge variant="secondary" className="mb-4 px-3 py-1 text-xs font-semibold tracking-wide uppercase border border-border">
            <UserCheck className="size-3.5 text-primary" />
            <span>Why Work Directly With Me</span>
          </Badge>
          
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-foreground font-serif">
            Independent SEO Consultant <br className="hidden sm:inline" />
            <span className="text-primary font-bold">vs. Traditional SEO Agency</span>
          </h2>
          
          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed font-sans">
            When you hire me, you do not get a junior account manager learning on your dime. 
            You get my personal, hands-on strategic expertise driving sustainable organic growth for your business.
          </p>
        </div>

        {/* Value Highlights Pill Row */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-12">
          <div className="flex items-center gap-3 p-4 rounded-lg bg-muted/50 border border-border">
            <div className="p-2.5 rounded-md bg-secondary text-foreground">
              <UserCheck className="size-5 text-primary" />
            </div>
            <div>
              <p className="text-sm font-semibold text-foreground">100% Direct Contact</p>
              <p className="text-xs text-muted-foreground">Always talk straight to Yassine</p>
            </div>
          </div>

          <div className="flex items-center gap-3 p-4 rounded-lg bg-muted/50 border border-border">
            <div className="p-2.5 rounded-md bg-secondary text-foreground">
              <PoundSterling className="size-5 text-primary" />
            </div>
            <div>
              <p className="text-sm font-semibold text-foreground">No Agency Overhead</p>
              <p className="text-xs text-muted-foreground">Budget goes 100% into execution</p>
            </div>
          </div>

          <div className="flex items-center gap-3 p-4 rounded-lg bg-muted/50 border border-border">
            <div className="p-2.5 rounded-md bg-secondary text-foreground">
              <Zap className="size-5 text-primary" />
            </div>
            <div>
              <p className="text-sm font-semibold text-foreground">Agile & Fast Execution</p>
              <p className="text-xs text-muted-foreground">No bureaucratic delays or sign-offs</p>
            </div>
          </div>
        </div>

        {/* Comparison Header Labels (Desktop) */}
        <div className="hidden lg:grid grid-cols-12 gap-6 items-center px-6 py-3 mb-4 rounded-lg bg-muted/60 text-sm font-medium text-muted-foreground">
          <div className="col-span-5 font-semibold text-foreground">Key Advantage & Comparison</div>
          <div className="col-span-4 text-primary font-semibold flex items-center gap-1.5">
            <Sparkles className="size-4" />
            Working With Me (Yassine Essaadi)
          </div>
          <div className="col-span-3 text-muted-foreground font-semibold flex items-center gap-1.5">
            <Building2 className="size-4" />
            Traditional SEO Agency
          </div>
        </div>

        {/* Comparison Cards Stack */}
        <motion.div className="space-y-6" variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.1
      }}>
          {comparisonPoints.map((point, index) => <motion.div key={point.id} data-index={index} variants={itemVariants}>
              <Card className="border border-border/80 shadow-xs hover:border-primary/30 transition-[border-color,box-shadow] duration-300">
                <CardContent className="p-6">
                  <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
                    
                    {/* Point & Highlight */}
                    <div className="lg:col-span-5 space-y-2">
                      <div className="flex items-center gap-2">
                        <span className="flex items-center justify-center size-6 rounded-full bg-secondary text-primary text-xs font-bold">
                          {index + 1}
                        </span>
                        <h3 className="text-lg font-semibold text-foreground font-serif">
                          {point.title}
                        </h3>
                      </div>
                      
                      {/* Pill Badge for Highlight */}
                      <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-secondary/80 text-foreground text-xs font-medium border border-accent/20">
                        <Sparkles className="size-3 text-accent" />
                        <span>{point.highlight}</span>
                      </div>
                    </div>

                    {/* Freelance Consultant Experience */}
                    <div className="lg:col-span-4 p-4 rounded-lg bg-secondary/30 border border-secondary text-card-foreground space-y-2">
                      <div className="flex items-center gap-2 text-xs font-semibold text-primary uppercase tracking-wider">
                        <Check className="size-4 text-primary shrink-0" />
                        <span>With Yassine (Freelance Consultant)</span>
                      </div>
                      <p className="text-sm text-foreground/90 leading-relaxed font-sans">
                        {point.consultant}
                      </p>
                    </div>

                    {/* Traditional Agency Experience */}
                    <div className="lg:col-span-3 p-4 rounded-lg bg-muted/40 border border-border text-muted-foreground space-y-2">
                      <div className="flex items-center gap-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                        <X className="size-4 text-destructive/80 shrink-0" />
                        <span>Traditional Agency</span>
                      </div>
                      <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed font-sans">
                        {point.agency}
                      </p>
                    </div>

                  </div>
                </CardContent>
              </Card>
            </motion.div>)}
        </motion.div>

        {/* CTA Card */}
        <div className="mt-12 lg:mt-16">
          <Card className="bg-primary text-primary-foreground border-none shadow-xl overflow-hidden relative">
            <div className="absolute right-0 top-0 bottom-0 w-1/3 bg-white/5 skew-x-12 pointer-events-none" />
            <CardContent className="p-8 sm:p-10 lg:p-12 relative z-10">
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
                
                <div className="lg:col-span-8 space-y-4">
                  <Badge variant="outline" className="text-primary-foreground border-primary-foreground/30 bg-primary-foreground/10 px-3 py-1 text-xs font-medium">
                    <ShieldCheck className="size-3.5" />
                    <span>Direct Partnership Guarantee</span>
                  </Badge>
                  
                  <h3 className="text-2xl sm:text-3xl font-bold tracking-tight text-primary-foreground font-serif">
                    Ready to work directly with an SEO expert who owns your results?
                  </h3>
                  
                  <p className="text-primary-foreground/90 text-sm sm:text-base max-w-2xl font-sans leading-relaxed">
                    No account managers, no inflated retainers, and no cookie-cutter reports. 
                    Let us discuss your search growth strategy in a free, straightforward consultation.
                  </p>
                </div>

                <div className="lg:col-span-4 flex flex-col sm:flex-row lg:flex-col gap-3 justify-end">
                  <Button size="lg" variant="secondary" asChild className="font-semibold text-foreground shadow-md hover:bg-secondary/90">
                    <Link to="/ContactPage">
                      <span>Book a Free SEO Consultation</span>
                      <ArrowRight className="size-4" />
                    </Link>
                  </Button>
                  
                  <Button size="lg" variant="outline" asChild className="border-primary-foreground/30 text-primary-foreground bg-primary-foreground/10 hover:bg-primary-foreground/20">
                    <Link to="/CaseStudiesPage">
                      <MessageSquare className="size-4" />
                      <span>Explore Case Studies</span>
                    </Link>
                  </Button>
                </div>

              </div>
            </CardContent>
          </Card>
        </div>

      </div>
    </section>;
}